/**
 * 1.6.0 AI content description: the workflow editor's part of For each shot, For each segment, Update segment, Call
 * webhook's JSON body options and Update metadata as a suggestion. Stream A's file (Builder.tsx, GraphCanvas.tsx and
 * NodeInspector.tsx only call in; docs/dev/mcp-1.6.0-build.md):
 *
 *  - aiStepIcons, describeAiStep: the canvas;
 *  - ForEachEditor (concurrency 1–8, at most N items, on a failed item; For each segment: the layer, only empty),
 *    UpdateSegmentEditor (the layer, text and values templated, "always a suggestion");
 *  - CallWebhookJsonOptions (below Call webhook's payload): the JSON body editor (parseBodyJson as you type), the time
 *    to answer, the media links' lifetime, and the template functions (TEMPLATE_FUNCTIONS) to insert; says when the
 *    endpoint may not receive media links;
 *  - UpdateMetadataSuggestionOption (below Update metadata's fields): "Write as suggestions to accept";
 *  - forEachItemPaths: {{item.*}} (and {{shot.*}}), and the answer's text and JSON of a Call webhook before it, for
 *    the Variables list of a step inside a For each body;
 *  - aiStepDefaults: a new step's fields.
 */
import { useMemo } from 'react';
import { ListVideo, Repeat2, SquarePen } from 'lucide-react';
import {
  FOR_EACH_LIMITS, MEDIA_LINK_TTL, TEMPLATE_FUNCTIONS, WEBHOOK_DEFAULT_TIMEOUT_SECONDS, forEachBodies, isLinkTemplateUsed, parseBodyJson,
  type AutomationGraph, type AutomationNode, type WebhookEndpointDTO,
} from '@prism/contracts';
import { useStrata } from '../../api/hooks';

type AiNode = Extract<AutomationNode, { type: 'for_each_shot' | 'for_each_segment' | 'update_segment' }>;
type Set = (patch: Partial<AutomationNode>) => void;

export const aiStepIcons = {
  for_each_shot: <ListVideo size={14} />,
  for_each_segment: <Repeat2 size={14} />,
  update_segment: <SquarePen size={14} />,
} as const;

/** The starter "Content description" layer (bootstrap). */
const DEFAULT_LAYER = 's_content';

export function aiStepDefaults(type: AiNode['type']): Record<string, unknown> {
  const loop = { concurrency: FOR_EACH_LIMITS.defaultConcurrency, maxItems: FOR_EACH_LIMITS.maxItems, onItemFailure: 'continue' };
  if (type === 'for_each_shot') return loop;
  if (type === 'for_each_segment') return { ...loop, stratumId: DEFAULT_LAYER, onlyEmpty: false };
  return { stratumId: DEFAULT_LAYER, text: '', values: [] };
}

export function describeAiStep(n: AiNode): string {
  if (n.type === 'for_each_shot') return `Each shot · ${n.concurrency} at a time`;
  if (n.type === 'for_each_segment') return `Each segment of ${n.stratumId}${n.onlyEmpty ? ' (empty ones)' : ''}`;
  return `${n.stratumId} · as a suggestion`;
}

function LayerSelect({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  const layers = (useStrata().data?.items ?? []).filter((s) => !s.system && !s.archived);
  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)} data-testid="ai-layer">
      {!layers.some((l) => l.id === value) && <option value={value}>{value}</option>}
      {layers.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
    </select>
  );
}

export function ForEachEditor({ node, set }: { node: Extract<AutomationNode, { type: 'for_each_shot' | 'for_each_segment' }>; set: Set }) {
  return (
    <>
      {node.type === 'for_each_segment' && (
        <>
          <div className="field"><label htmlFor="fe-layer">Layer</label>
            <LayerSelect id="fe-layer" value={node.stratumId} onChange={(v) => set({ stratumId: v } as Partial<AutomationNode>)} />
          </div>
          <label className="check"><input type="checkbox" checked={node.onlyEmpty} onChange={(e) => set({ onlyEmpty: e.target.checked } as Partial<AutomationNode>)} /> Only segments without text or values</label>
        </>
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="fe-conc">At a time</label>
          <input id="fe-conc" className="input" type="number" min={1} max={FOR_EACH_LIMITS.maxConcurrency} value={node.concurrency}
            onChange={(e) => set({ concurrency: Math.max(1, Math.min(FOR_EACH_LIMITS.maxConcurrency, Number(e.target.value) || 1)) } as Partial<AutomationNode>)} />
        </div>
        <div className="field"><label htmlFor="fe-max">At most</label>
          <input id="fe-max" className="input" type="number" min={1} max={FOR_EACH_LIMITS.maxItems} value={node.maxItems}
            onChange={(e) => set({ maxItems: Math.max(1, Math.min(FOR_EACH_LIMITS.maxItems, Number(e.target.value) || 1)) } as Partial<AutomationNode>)} />
        </div>
        <div className="field"><label htmlFor="fe-fail">When an item fails</label>
          <select id="fe-fail" className="select" value={node.onItemFailure} onChange={(e) => set({ onItemFailure: e.target.value } as Partial<AutomationNode>)}>
            <option value="continue">Go on with the others</option>
            <option value="fail">Stop the step</option>
          </select>
        </div>
      </div>
      <span className="help">
        Connect the steps to run for each {node.type === 'for_each_shot' ? 'shot (from shot detection)' : 'segment'} to the <b>each</b> output; they see
        {' '}<span className="mono">{'{{item.index}}'}</span>, <span className="mono">{'{{item.startTimecode}}'}</span>, <span className="mono">{'{{item.text}}'}</span> (
        {node.type === 'for_each_shot' ? 'the dialogue in the shot' : 'the segment’s text'}) and more. <b>next</b> continues once every item is done.
        At most {FOR_EACH_LIMITS.maxConcurrency} at a time and {FOR_EACH_LIMITS.maxItems} items in a run; cancelling the run stops it.
      </span>
    </>
  );
}

export function UpdateSegmentEditor({ node, set }: { node: Extract<AutomationNode, { type: 'update_segment' }>; set: Set }) {
  return (
    <>
      <div className="field"><label htmlFor="us-layer">Layer</label>
        <LayerSelect id="us-layer" value={node.stratumId} onChange={(v) => set({ stratumId: v } as Partial<AutomationNode>)} />
      </div>
      <div className="field"><label htmlFor="us-text">Text</label>
        <textarea id="us-text" className="input mono" rows={3} value={node.text} placeholder="{{nodes.ai.output.json.description}}" onChange={(e) => set({ text: e.target.value } as Partial<AutomationNode>)} data-testid="us-text" />
      </div>
      <div className="field"><label htmlFor="us-values">Values</label>
        <input id="us-values" className="input mono" value={node.values.join(' | ')} placeholder="{{nodes.ai.output.json.tags}}"
          onChange={(e) => set({ values: e.target.value.split('|').map((v) => v.trim()).filter(Boolean) } as Partial<AutomationNode>)} />
        <span className="help">For vocabulary and tags layers; separate templates with |. A list renders comma separated and is split into values.</span>
      </div>
      <span className="help">
        Writes into the current shot’s or segment’s segment on the layer (made from the shot if there is none), always as an <b>AI suggestion</b> that
        people accept or reject in the Log tab. A segment a person wrote is never changed: that item is counted as skipped.
      </span>
    </>
  );
}

export function CallWebhookJsonOptions({ node, set, endpoints }: { node: Extract<AutomationNode, { type: 'call_webhook' }>; set: Set; endpoints: WebhookEndpointDTO[] }) {
  const json = node.bodyJson !== undefined;
  const parsed = useMemo(() => (json ? parseBodyJson(node.bodyJson ?? '') : null), [json, node.bodyJson]);
  const endpoint = endpoints.find((e) => e.id === node.endpointId);
  const usesLinks = [node.path ?? '', node.bodyJson ?? '', ...Object.values(node.payload)].some(isLinkTemplateUsed);
  const insert = (snippet: string) => set({ bodyJson: `${node.bodyJson ?? ''}${snippet}` } as Partial<AutomationNode>);
  return (
    <div className="ai-webhook-options" data-testid="webhook-json-options">
      <label className="check">
        <input type="checkbox" checked={json} data-testid="webhook-json-toggle"
          onChange={(e) => set({ bodyJson: e.target.checked ? '{\n  "text": "{{asset.title}}"\n}' : undefined } as Partial<AutomationNode>)} />
        Send a JSON body instead of the payload (for AI services and other APIs)
      </label>
      {json && (
        <div className="field"><label htmlFor="n-ep-json">JSON body</label>
          <textarea id="n-ep-json" className="input mono ai-json-body" rows={12} spellCheck={false} value={node.bodyJson ?? ''} onChange={(e) => set({ bodyJson: e.target.value } as Partial<AutomationNode>)} data-testid="webhook-json-body" />
          {parsed && !parsed.ok
            ? <span className="help error" role="alert" data-testid="webhook-json-error">{parsed.error}</span>
            : <span className="help">Templates go inside strings (<span className="mono">{'"{{item.text}}"'}</span>); <span className="mono">{'{{json path}}'}</span> inserts a value as JSON (a list, an object). The answer, up to 1 MB, is <span className="mono">{`{{nodes.${node.id}.output.response…}}`}</span>; an AI service’s text is <span className="mono">{`…output.text`}</span> and, when it is JSON, <span className="mono">{`…output.json.<key>`}</span>.</span>}
          <div className="ai-template-functions">
            {Object.entries(TEMPLATE_FUNCTIONS).map(([name, f]) => (
              <button key={name} type="button" className="btn ghost sm mono" title={f.label}
                onClick={() => insert(`{{${name}${f.args.length ? ` ${f.args.map((a) => (a === 'shotIndex' ? 'item.index' : a === 'seconds' ? 'item.middleSeconds' : a.replace('?', ''))).filter((a) => a !== 'position').join(' ')}` : ''}}}`)}>
                {name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="field-row">
        <div className="field"><label htmlFor="n-ep-timeout">Time to answer (s)</label>
          <input id="n-ep-timeout" className="input" type="number" min={1} max={120} value={node.requestTimeoutSeconds ?? ''} placeholder={String(WEBHOOK_DEFAULT_TIMEOUT_SECONDS)}
            onChange={(e) => set({ requestTimeoutSeconds: e.target.value ? Math.max(1, Math.min(120, Number(e.target.value))) : undefined } as Partial<AutomationNode>)} />
        </div>
        <div className="field"><label htmlFor="n-ep-ttl">Media links valid (min)</label>
          <input id="n-ep-ttl" className="input" type="number" min={1} max={MEDIA_LINK_TTL.maxSeconds / 60} value={node.mediaLinkTtlSeconds ? node.mediaLinkTtlSeconds / 60 : ''} placeholder={String(MEDIA_LINK_TTL.defaultSeconds / 60)}
            onChange={(e) => set({ mediaLinkTtlSeconds: e.target.value ? Math.max(MEDIA_LINK_TTL.minSeconds, Math.min(MEDIA_LINK_TTL.maxSeconds, Number(e.target.value) * 60)) : undefined } as Partial<AutomationNode>)} />
        </div>
      </div>
      {usesLinks && (
        <span className={`help ${endpoint && !endpoint.mayReceiveMediaLinks ? 'error' : ''}`} data-testid="webhook-links-note">
          {endpoint && !endpoint.mayReceiveMediaLinks
            ? `“${endpoint.name}” may not receive media links: allow it in Administration → Webhooks, or the step cannot be published.`
            : 'Media links are signed, work only for this asset, expire, and each use is logged.'}
        </span>
      )}
    </div>
  );
}

export function UpdateMetadataSuggestionOption({ node, set }: { node: Extract<AutomationNode, { type: 'update_metadata' }>; set: Set }) {
  return (
    <label className="check" data-testid="metadata-as-suggestion">
      <input type="checkbox" checked={Boolean(node.asSuggestion)} onChange={(e) => set({ asSuggestion: e.target.checked || undefined } as Partial<AutomationNode>)} />
      Write as suggestions to accept (people accept or reject them on the Metadata tab)
    </label>
  );
}

const ITEM_PATHS: Array<[string, string]> = [
  ['index', '0-based number (for shotFrameUrl)'], ['number', '1-based number'], ['count', 'How many items'], ['startTimecode', 'Start timecode'],
  ['endTimecode', 'End timecode'], ['startSeconds', 'Start (s)'], ['middleSeconds', 'Middle (s)'], ['durationSeconds', 'Length (s)'], ['text', 'Dialogue or text'],
  ['values', 'Values (a segment’s)'],
];

/** {{item.*}} paths for a step inside a For each body (none outside one). */
export function forEachItemPaths(graph: AutomationGraph, node: AutomationNode): Array<{ path: string; label: string }> {
  for (const [loopId, body] of forEachBodies(graph)) {
    if (!body.has(node.id)) continue;
    const loop = graph.nodes.find((n) => n.id === loopId);
    const shots = loop?.type === 'for_each_shot';
    const paths = ITEM_PATHS.map(([k, label]) => ({ path: `item.${k}`, label: `${shots ? 'Shot' : 'Segment'} · ${label}` }));
    const webhooks = graph.nodes.filter((n) => n.type === 'call_webhook' && body.has(n.id) && n.id !== node.id);
    return [
      ...paths,
      ...webhooks.flatMap((w) => [
        { path: `nodes.${w.id}.output.text`, label: `${w.label || 'Call webhook'} · the AI’s answer text` },
        { path: `nodes.${w.id}.output.json.<key>`, label: `${w.label || 'Call webhook'} · a value of its JSON answer` },
      ]),
    ];
  }
  return [];
}
